import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { paymentApi, type RecordPaymentInput } from "@/lib/payment.api";
import { getApiErrorCode, getApiErrorMessage } from "@/lib/api-error";
import { queryKeys } from "@/lib/query-keys";

const ERROR_MESSAGES: Record<string, string> = {
  INSUFFICIENT_CREDIT_BALANCE: "Crédit disponible insuffisant pour ce paiement.",
};

// onOverpaymentRequired is passed in by the dialog for the same reason as
// useIssueInvoiceMutation's onStockConflict — bespoke branching UI state
// stays component-local, the mutation stays reusable.
export function useRecordPaymentMutation(invoiceId: number, onOverpaymentRequired: (amount: number) => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RecordPaymentInput) => paymentApi.record(invoiceId, input).then((r) => r.data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.invoice(invoiceId) });
      // A credit-method payment just spent the party's credit — refresh it
      // wherever it's shown (Party detail's balance/lots) as well.
      if (variables.method === "credit") {
        queryClient.invalidateQueries({ queryKey: ["credits"] });
      }
    },
    onError: (error, variables) => {
      const code = getApiErrorCode(error);
      if (code === "OVERPAYMENT_CHOICE_REQUIRED") {
        onOverpaymentRequired(variables.amountTendered);
        return;
      }
      toast.error((code && ERROR_MESSAGES[code]) ?? getApiErrorMessage(error, "Erreur lors de l'enregistrement."));
    },
  });
}
